<template>
  <GrowTransition :style="transitionStyle">
    <div
      :class="['popover', position, { 'popover--has-footer': !!$slots.footer }]"
      :style="popoverStyle"
    >
      <slot />
      <footer v-if="$slots.footer" class="popover__footer">
        <slot name="footer" />
      </footer>
      <div class="popover__arrow" />
    </div>
  </GrowTransition>
</template>

<script>
import GrowTransition from '@/components/transitions/Grow';

export default {
  components: {
    GrowTransition,
  },

  props: {
    /**
     * Popover position based on the closest relative parent, accepts:
     * `top start`, `top center`, `top end`, `bottom start`, `bottom center`, `bottom end`
     */
    position: {
      type: String,
      default: 'top center',
    },
  },

  data() {
    return {
      popoverStyle: {},
    };
  },

  computed: {
    // Transition in the opposite direction of the popover arrow
    transitionStyle() {
      const position = this.position.split(' ');
      const x = { top: 'bottom', bottom: 'top' }[position[0]];
      const y = { start: 'left', end: 'right' }[position[1]];

      return {
        transformOrigin: `${x} ${y}`,
      };
    },
  },
};
</script>

<style lang="scss">
$popover-arrow-size: 20px;
$popover-border-color: $alt-200;

.popover {
  width: max-content;

  position: absolute;
  z-index: 2;

  color: $text-color;
  font-size: $font-size-default;
  font-family: $font-family-default;
  line-height: $line-height-default;

  background-color: white;
  border-radius: $border-radius-default;
  border: 1px solid $popover-border-color;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);

  &.top {
    bottom: 100%;
    margin-bottom: $spacing-unit-small;
  }
  &.bottom {
    top: 100%;
    margin-top: $spacing-unit-small;
  }
  &.start {
    left: 0;
    margin-right: $spacing-unit-small;
  }
  &.end {
    right: 0;
    margin-left: $spacing-unit-small;
  }
  &.center {
    left: 50%;
    transform: translateX(-50%);
  }
}

.popover--full {
  max-width: 100%;
}

.popover__arrow {
  position: absolute;
  z-index: 3;

  .popover--no-arrow & {
    display: none;
  }

  .bottom & {
    top: -$popover-arrow-size;

    &::before {
      border-bottom-color: $popover-border-color;
      filter: none;
    }
    &::after {
      top: 4px;
      border-bottom-color: white;
    }
  }

  .top & {
    top: 100%;

    &::before {
      border-top-color: $popover-border-color;
    }
    &::after {
      border-top-color: white;

      @at-root .popover.top .popover__footer ~ .popover__arrow::after {
        border-top-color: $alt-50;
      }
    }
  }

  .end & {
    right: $spacing-unit-small + $popover-arrow-size;
  }

  .start & {
    left: $spacing-unit-small;
  }

  .center & {
    left: calc(50% - #{$popover-arrow-size/2});
  }

  &::before,
  &::after {
    content: '';
    display: block;
    width: 0;
    height: 0;

    position: absolute;
    top: 0;
    left: 0;

    border: $popover-arrow-size/2 solid transparent;
    transform-origin: top left;
  }

  &::before {
    filter: drop-shadow(0 6px 4px rgba(0, 0, 0, 0.15));
  }

  &::after {
    left: 2px;

    border: ($popover-arrow-size / 2 - 2px) solid transparent;
  }
}
</style>

<style lang="scss">
.popover__body,
.popover__footer {
  padding: $spacing-unit-small $spacing-unit-default;
}

.popover__footer {
  width: 100%;

  border-top: 1px solid $alt-100;
  border-bottom-right-radius: $border-radius-default;
  border-bottom-left-radius: $border-radius-default;
  background-color: $alt-50;
}
</style>
